import { z } from 'zod'
import { Instant } from './primitives'

export const AI_CREDENTIAL_PROVIDERS = ['vertex_gemini', 'typesafe_jev', 'anthropic_claude'] as const
export const AiCredentialProvider = z.enum(AI_CREDENTIAL_PROVIDERS)
export type AiCredentialProvider = z.infer<typeof AiCredentialProvider>

export const DEFAULT_VERTEX_LOCATION = 'us-central1'
export const VERTEX_CHAT_MODEL_CHOICES = ['gemini-2.5-flash', 'gemini-2.5-pro'] as const
export const VERTEX_EMBEDDING_MODEL_CHOICES = ['gemini-embedding-001'] as const
export const DEFAULT_TYPESAFE_MODEL = 'jev-latest'
export const ANTHROPIC_CHAT_MODEL_CHOICES = ['claude-opus-5-5', 'claude-sonnet-5-5', 'claude-haiku-5-5'] as const
export const DEFAULT_ANTHROPIC_CHAT_MODEL = 'claude-opus-5-5'

export const VERTEX_PROJECT_ID_PATTERN = /^[a-z][a-z0-9-]{4,28}[a-z0-9]$/
export const VERTEX_LOCATION_PATTERN = /^[a-z]+(-[a-z]+)*[0-9]*$/
export const SERVICE_ACCOUNT_EMAIL_SUFFIX = '.iam.gserviceaccount.com'
export const PRIVATE_KEY_PEM_PATTERN = /^-----BEGIN PRIVATE KEY-----\s[\s\S]+\s-----END PRIVATE KEY-----\s*$/

export const ANTHROPIC_API_KEY_PATTERN = /^[\x21-\x7e]+$/

const MAX_SETTING_CHARS = 200
const MAX_SECRET_CHARS = 8192

const Setting = z.string().trim().min(1).max(MAX_SETTING_CHARS)

export const VertexChatModel = z.enum(VERTEX_CHAT_MODEL_CHOICES)
export const VertexEmbeddingModel = z.enum(VERTEX_EMBEDDING_MODEL_CHOICES)
export const AnthropicChatModel = z.enum(ANTHROPIC_CHAT_MODEL_CHOICES)

export const VertexCredentialsUpdate = z.strictObject({
  projectId: z.string().trim().regex(VERTEX_PROJECT_ID_PATTERN),
  location: z.string().trim().regex(VERTEX_LOCATION_PATTERN).max(MAX_SETTING_CHARS).default(DEFAULT_VERTEX_LOCATION),
  clientEmail: z.email().trim().max(MAX_SETTING_CHARS).endsWith(SERVICE_ACCOUNT_EMAIL_SUFFIX),
  privateKey: z.string().max(MAX_SECRET_CHARS).regex(PRIVATE_KEY_PEM_PATTERN).optional(),
  chatModel: VertexChatModel,
  embeddingModel: VertexEmbeddingModel,
})
export type VertexCredentialsUpdate = z.infer<typeof VertexCredentialsUpdate>

export const TypeSafeCredentialsUpdate = z.strictObject({
  apiKey: z.string().trim().min(1).max(MAX_SECRET_CHARS).optional(),
  model: Setting.default(DEFAULT_TYPESAFE_MODEL),
})
export type TypeSafeCredentialsUpdate = z.infer<typeof TypeSafeCredentialsUpdate>

export const AnthropicCredentialsUpdate = z.strictObject({
  apiKey: z.string().trim().max(MAX_SECRET_CHARS).regex(ANTHROPIC_API_KEY_PATTERN).optional(),
  model: AnthropicChatModel,
})
export type AnthropicCredentialsUpdate = z.infer<typeof AnthropicCredentialsUpdate>

const CredentialStamps = { secretUpdatedAt: Instant, verifiedAt: Instant }

export const VertexCredentialsView = z.object({
  projectId: z.string(),
  location: z.string(),
  clientEmail: z.string(),
  chatModel: VertexChatModel,
  embeddingModel: VertexEmbeddingModel,
  ...CredentialStamps,
})
export type VertexCredentialsView = z.infer<typeof VertexCredentialsView>

export const TypeSafeCredentialsView = z.object({
  model: z.string(),
  ...CredentialStamps,
})
export type TypeSafeCredentialsView = z.infer<typeof TypeSafeCredentialsView>

export const AnthropicCredentialsView = z.object({
  model: z.string(),
  usable: z.boolean(),
  ...CredentialStamps,
})
export type AnthropicCredentialsView = z.infer<typeof AnthropicCredentialsView>

export const AiCredentialsSettings = z.object({
  vertex: VertexCredentialsView.nullable(),
  typesafe: TypeSafeCredentialsView.nullable(),
  anthropic: AnthropicCredentialsView.nullable(),
  aiAvailable: z.boolean(),
})
export type AiCredentialsSettings = z.infer<typeof AiCredentialsSettings>
